import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { useAuth } from 'hooks'
import React from 'react'
import { Link as LinkRouter } from 'react-router-dom'
import { Button as ButtonUI } from '../ui/button'

/**
 * A cor entra por variável, não por classe: `--cor` é a cor da paleta e
 * `--cor-texto` o que fica legível sobre ela. Assim cada variante tem uma
 * regra só, em vez de uma combinação `variante × cor`.
 */
const cores = {
    primary: '[--cor:var(--primary)] [--cor-texto:var(--primary-foreground)]',
    secondary:
        '[--cor:var(--secondary)] [--cor-texto:var(--secondary-foreground)]',
    error: '[--cor:var(--error)] [--cor-texto:var(--color-primary-foreground)]',
    warning:
        '[--cor:var(--warning)] [--cor-texto:var(--color-secondary-foreground)]',
    success:
        '[--cor:var(--success)] [--cor-texto:var(--color-secondary-foreground)]',
    info: '[--cor:var(--info)] [--cor-texto:var(--color-secondary-foreground)]',
    inherit: '[--cor:currentColor] [--cor-texto:var(--background)]',
} as const

/** Escurece a cor no hover, como o `palette[cor].dark` do MUI. */
const HOVER_CONTAINED =
    'hover:bg-[color-mix(in_oklab,var(--cor)_85%,var(--color-black))]'
/** Realce translúcido do `outlined`/`text` do MUI. */
const HOVER_PLANO = 'hover:bg-[color-mix(in_oklab,var(--cor)_10%,transparent)]'

/**
 * `href` vira `<a>`, e âncora não tem atributo `disabled` — o estado é marcado
 * com `aria-disabled`. Por isso cada regra de desabilitado aparece duas vezes.
 */
const DESABILITADO = [
    'aria-disabled:pointer-events-none aria-disabled:opacity-50',
    'disabled:bg-muted disabled:text-muted-foreground disabled:border-transparent disabled:shadow-none',
    'aria-disabled:bg-muted aria-disabled:text-muted-foreground aria-disabled:border-transparent aria-disabled:shadow-none',
]

/**
 * O que o `ui/button` não dá: a geometria do override de `MuiButton` do
 * `theme.ts` — raio de 0.28rem, padding de 0.5rem/1.5rem, fonte de 0.75rem
 * em caixa alta — e a paleta por `color`, que o MUI tinha e o `ui/button` não.
 */
const estiloMui = cva(
    [
        'h-auto min-w-16 rounded-sm px-6 py-2',
        'text-sm tracking-tight uppercase font-medium',
        '[&_svg]:size-5',
        ...DESABILITADO,
    ],
    {
        variants: {
            variant: {
                contained: ['bg-(--cor) text-(--cor-texto)', HOVER_CONTAINED],
                outlined: [
                    'border-(--cor)/50 text-(--cor)',
                    'hover:border-(--cor)',
                    HOVER_PLANO,
                ],
                text: ['text-(--cor)', HOVER_PLANO],
            },
            color: cores,
            /** `medium` é a geometria do MUI; `large` é o botão de destaque. */
            size: {
                medium: '',
                large: 'h-14 text-lg font-semibold',
            },
            fullWidth: { true: 'w-full' },
        },
        defaultVariants: {
            variant: 'contained',
            color: 'primary',
            size: 'medium',
        },
    },
)

/** Variante do MUI → variante equivalente do `ui/button`. */
const variantesUI = {
    contained: 'default',
    outlined: 'outline',
    text: 'ghost',
} as const

type ButtonProps = Omit<React.ComponentProps<'button'>, 'color'> &
    Omit<VariantProps<typeof estiloMui>, 'fullWidth'> & {
        /** Só renderiza o botão para estas categorias de usuário. */
        can?: number | number[]
        href?: string | number
        startIcon?: React.ReactNode
        endIcon?: React.ReactNode
        fullWidth?: boolean
    }

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            can: onlyView,
            href,
            variant = 'contained',
            color,
            size,
            fullWidth,
            startIcon,
            endIcon,
            className,
            children,
            disabled,
            type = 'button',
            ...props
        },
        ref,
    ) => {
        const {
            decoded: { categoria },
        } = useAuth()

        if (onlyView) {
            if (typeof onlyView === 'number' && onlyView !== categoria) {
                return null
            }

            if (Array.isArray(onlyView) && !onlyView.includes(categoria)) {
                return null
            }
        }

        const classes = cn(
            estiloMui({ variant, color, size, fullWidth }),
            className,
        )
        const varianteUI = variantesUI[variant ?? 'contained']

        const conteudo = (
            <>
                {startIcon}
                {children}
                {endIcon}
            </>
        )

        if (href !== undefined) {
            return (
                <ButtonUI asChild variant={varianteUI} className={classes}>
                    <LinkRouter
                        {...(props as object)}
                        to={String(href)}
                        ref={ref as unknown as React.Ref<HTMLAnchorElement>}
                        aria-disabled={disabled || undefined}
                    >
                        {conteudo}
                    </LinkRouter>
                </ButtonUI>
            )
        }

        return (
            <ButtonUI
                {...props}
                ref={ref}
                type={type}
                disabled={disabled}
                variant={varianteUI}
                className={classes}
            >
                {conteudo}
            </ButtonUI>
        )
    },
)

Button.displayName = 'Button'
